import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog, { type ModalDialogButtonSetting } from '../../modal-dialog/dialog.component';
import { DynamicMyoshLogo } from '../../myosh-logo/dynamic-myosh-logo';

const userTimeZone = Intl.DateTimeFormat(navigator.language).resolvedOptions().timeZone;

export interface AboutInfoSchema {
  text: string;
}

export interface AboutInfoModalProps {
  version?: string;
  copyright?: string;
  currentSchema?: AboutInfoSchema;
  region?: string;
  cluster?: string;
  schemaTimeZone?: string;
  isAboutInfoOpen?: boolean;
  onClose?: () => void;
  logoSource?: string;
  controllingSuperuser?: string;
  showUserTimeZone?: boolean;
  openSourceNotices?: string;
}

const AboutInfoModal = ({
  region = 'n/a',
  cluster = 'n/a',
  schemaTimeZone = 'n/a',
  copyright,
  currentSchema,
  isAboutInfoOpen,
  onClose,
  logoSource,
  controllingSuperuser,
  version,
  showUserTimeZone = false,
  openSourceNotices,
}: AboutInfoModalProps) => {
  const { t } = useTranslation();

  const buttons = useMemo<Array<ModalDialogButtonSetting>>(
    () => [{ name: 'ok', text: t('ok'), closeModalOnClick: true }],
    [t]
  );

  return (
    <Dialog
      header={t('about')}
      visible={Boolean(isAboutInfoOpen)}
      onClose={onClose}
      buttons={buttons}
      showCloseButton={false}
      className="max-w-72"
    >
      <div className="flex h-full flex-col items-center gap-4">
        {logoSource ? <img src={logoSource} className="w-52" alt="Logo" /> : <DynamicMyoshLogo className="w-52" />}
        <div className="flex flex-col gap-2">
          <span>Version: {version}</span>
          <span>Current company: {currentSchema?.text || 'n/a'}</span>
          <span>Region: {region}</span>
          <span>Cluster: {cluster}</span>
          <span>Schema Time Zone: {schemaTimeZone}</span>
          {showUserTimeZone && <span>User Time Zone: {userTimeZone}</span>}
          {controllingSuperuser && <span>Controlling superuser: {controllingSuperuser}</span>}
          <span>{copyright || `${t('copyright')} ${new Date().getFullYear()}`}</span>
          {openSourceNotices && <span className="mt-4 text-xs">{openSourceNotices}</span>}
        </div>
      </div>
    </Dialog>
  );
};

export default AboutInfoModal;
